import React, { useEffect, useRef, useState } from "react"
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Stack, TextField, Tooltip, ToggleButton, ToggleButtonGroup
} from "@mui/material"
import FormatBoldIcon from "@mui/icons-material/FormatBold"
import FormatItalicIcon from "@mui/icons-material/FormatItalic"
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered"
import LinkIcon from "@mui/icons-material/Link"
import LinkOffIcon from "@mui/icons-material/LinkOff"
import TitleIcon from "@mui/icons-material/Title"
import SubtitlesIcon from "@mui/icons-material/Subtitles"
import NotesIcon from "@mui/icons-material/Notes"
import TableChartIcon from "@mui/icons-material/TableChart"
import CodeIcon from "@mui/icons-material/Code"

// Minimal WYSIWYG editor styled with MUI.
// Uses document.execCommand on a contentEditable div — deprecated but still
// works in all evergreen browsers and avoids pulling in a 200kB dependency.
//
// Supported: bold, italic, underline, h2, h3, paragraph, ul, ol, link, unlink,
// table insertion, raw HTML toggle.
//
// `value` is HTML string; `onChange` receives the updated innerHTML.
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 200 }) {
    const ref = useRef(null)
    const [, force] = useState(0)
    const [rawMode, setRawMode] = useState(false)
    const [tableDialogOpen, setTableDialogOpen] = useState(false)
    const [rawDraft, setRawDraft] = useState(value || "")

    // Keep DOM in sync only when external value diverges from current innerHTML.
    // This avoids cursor-jump on every keystroke (we don't re-set innerHTML
    // while the user is typing).
    useEffect(() => {
        if (rawMode || !ref.current) return
        const incoming = value || ""
        if (ref.current.innerHTML !== incoming) {
            ref.current.innerHTML = incoming
        }
    }, [value, rawMode])

    function exec(command, arg) {
        if (!ref.current) return
        ref.current.focus()
        document.execCommand(command, false, arg)
        if (onChange) onChange(ref.current.innerHTML)
        force(n => n + 1)
    }

    function handleInput() {
        if (!onChange || !ref.current) return
        onChange(ref.current.innerHTML)
    }

    function handleLink() {
        const url = window.prompt("Enter URL", "https://")
        if (url == null || url.trim() === "") return
        exec("createLink", url.trim())
    }

    function handleHeading(level) {
        exec("formatBlock", level)
    }

    // Inserts an HTML fragment at the current selection. Falls back to
    // appending at the end when the editor isn't focused yet.
    function insertHtml(html) {
        if (!ref.current) return
        ref.current.focus()
        const ok = document.execCommand("insertHTML", false, html)
        if (!ok) {
            ref.current.innerHTML += html
        }
        if (onChange) onChange(ref.current.innerHTML)
        force(n => n + 1)
    }

    function buildTableHtml(rows, cols) {
        const r = Math.max(1, Math.min(20, rows))
        const c = Math.max(1, Math.min(10, cols))
        const headerCells = Array.from({ length: c }, () =>
            `<td colspan="1" rowspan="1"><p>Header</p></td>`).join("")
        const bodyRow = Array.from({ length: c }, () =>
            `<td colspan="1" rowspan="1"><p>Cell</p></td>`).join("")
        const bodyRows = Array.from({ length: r - 1 }, () => `<tr>${bodyRow}</tr>`).join("")
        return `<table data-number-column="false"><tbody><tr class="table-header">${headerCells}</tr>${bodyRows}</tbody></table><p><br/></p>`
    }

    function handleInsertTable(rows, cols) {
        insertHtml(buildTableHtml(rows, cols))
        setTableDialogOpen(false)
    }

    // When toggling to raw mode — capture the current innerHTML into a
    // textarea-backed draft. When toggling back — apply edits to contentEditable.
    function toggleRawMode() {
        if (!rawMode) {
            setRawDraft(ref.current ? ref.current.innerHTML : (value || ""))
            setRawMode(true)
        } else {
            // Apply textarea content back to the editor + state.
            if (onChange) onChange(rawDraft)
            setRawMode(false)
            // Re-render after DOM swaps back; the useEffect above will repopulate.
            force(n => n + 1)
        }
    }

    function handleRawChange(e) {
        setRawDraft(e.target.value)
        if (onChange) onChange(e.target.value)
    }

    return (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, overflow: "hidden", background: "#fff" }}>
            {/* Toolbar */}
            <Stack direction="row" alignItems="center" sx={{ p: 0.5, gap: 0.5, flexWrap: "wrap", background: "#fafafa", borderBottom: "1px solid", borderColor: "divider" }}>
                <ToggleButtonGroup size="small" exclusive>
                    <Tooltip title="Paragraph">
                        <ToggleButton value="p" disabled={rawMode} onClick={() => handleHeading("P")}>
                            <NotesIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Heading 2">
                        <ToggleButton value="h2" disabled={rawMode} onClick={() => handleHeading("H2")}>
                            <TitleIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Heading 3">
                        <ToggleButton value="h3" disabled={rawMode} onClick={() => handleHeading("H3")}>
                            <SubtitlesIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>

                <Divider orientation="vertical" flexItem />

                <ToggleButtonGroup size="small">
                    <Tooltip title="Bold (Ctrl+B)">
                        <ToggleButton value="bold" disabled={rawMode} selected={!rawMode && isActive("bold")} onClick={() => exec("bold")}>
                            <FormatBoldIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Italic (Ctrl+I)">
                        <ToggleButton value="italic" disabled={rawMode} selected={!rawMode && isActive("italic")} onClick={() => exec("italic")}>
                            <FormatItalicIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Underline (Ctrl+U)">
                        <ToggleButton value="underline" disabled={rawMode} selected={!rawMode && isActive("underline")} onClick={() => exec("underline")}>
                            <FormatUnderlinedIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>

                <Divider orientation="vertical" flexItem />

                <ToggleButtonGroup size="small">
                    <Tooltip title="Bulleted list">
                        <ToggleButton value="ul" disabled={rawMode} onClick={() => exec("insertUnorderedList")}>
                            <FormatListBulletedIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Numbered list">
                        <ToggleButton value="ol" disabled={rawMode} onClick={() => exec("insertOrderedList")}>
                            <FormatListNumberedIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>

                <Divider orientation="vertical" flexItem />

                <ToggleButtonGroup size="small">
                    <Tooltip title="Insert link">
                        <ToggleButton value="link" disabled={rawMode} onClick={handleLink}>
                            <LinkIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Remove link">
                        <ToggleButton value="unlink" disabled={rawMode} onClick={() => exec("unlink")}>
                            <LinkOffIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Insert table">
                        <ToggleButton value="table" disabled={rawMode} onClick={() => setTableDialogOpen(true)}>
                            <TableChartIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>

                <Box sx={{ flex: 1 }} />

                <Tooltip title={rawMode ? "Back to visual editor" : "Edit raw HTML"}>
                    <ToggleButton size="small" value="raw" selected={rawMode} onClick={toggleRawMode}>
                        <CodeIcon fontSize="small" />
                    </ToggleButton>
                </Tooltip>
            </Stack>

            {/* Canvas — visual mode */}
            {!rawMode && (
                <Box
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleInput}
                    onBlur={handleInput}
                    data-placeholder={placeholder || "Start typing…"}
                    sx={{
                        p: 2,
                        minHeight,
                        outline: "none",
                        fontSize: 14,
                        lineHeight: 1.55,
                        "& p": { my: 0.5 },
                        "& h2": { fontSize: 22, fontWeight: 700, my: 1.5 },
                        "& h3": { fontSize: 18, fontWeight: 700, my: 1 },
                        "& a": { color: "var(--xp-orange, #F05125)", textDecoration: "underline" },
                        "& ul, & ol": { pl: 3, my: 1 },
                        // Visible table styling while editing — final styling is up to renderer.
                        "& table": {
                            borderCollapse: "collapse",
                            width: "100%",
                            my: 1.5,
                            border: "1px solid #ccc",
                        },
                        "& th, & td": {
                            border: "1px solid #ddd",
                            padding: "6px 10px",
                            verticalAlign: "top",
                            minWidth: 40,
                        },
                        "& tr.table-header td, & thead td": {
                            background: "#fff1ec",
                            fontWeight: 700,
                        },
                        "&[data-empty='true']::before": {
                            content: "attr(data-placeholder)",
                            color: "text.disabled",
                            pointerEvents: "none",
                        },
                    }}
                />
            )}

            {/* Raw HTML mode — monospace textarea */}
            {rawMode && (
                <TextField
                    multiline
                    minRows={Math.max(8, Math.round(minHeight / 24))}
                    fullWidth
                    value={rawDraft}
                    onChange={handleRawChange}
                    placeholder="<p>HTML…</p>"
                    InputProps={{
                        sx: {
                            fontFamily: "monospace",
                            fontSize: 12,
                            lineHeight: 1.5,
                            borderRadius: 0,
                            "& textarea": { whiteSpace: "pre" },
                        },
                    }}
                    sx={{ "& fieldset": { border: 0 } }}
                />
            )}

            {/* Insert-table dialog */}
            <TableDialog
                open={tableDialogOpen}
                onClose={() => setTableDialogOpen(false)}
                onSubmit={handleInsertTable}
            />
        </Box>
    )
}

function TableDialog({ open, onClose, onSubmit }) {
    const [rows, setRows] = useState(3)
    const [cols, setCols] = useState(3)
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Insert table</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Rows (incl. header)"
                        type="number"
                        value={rows}
                        onChange={e => setRows(parseInt(e.target.value, 10) || 1)}
                        inputProps={{ min: 1, max: 20 }}
                    />
                    <TextField
                        label="Columns"
                        type="number"
                        value={cols}
                        onChange={e => setCols(parseInt(e.target.value, 10) || 1)}
                        inputProps={{ min: 1, max: 10 }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="warning" onClick={() => onSubmit(rows, cols)}>
                    Insert
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// Best-effort active-state probe. Some browsers throw on queryCommandState
// for certain commands — wrap in try/catch and assume false.
function isActive(cmd) {
    try { return !!document.queryCommandState(cmd) }
    catch { return false }
}
